import React from 'react'
import _ from 'lodash'
import { useDispatch } from 'react-redux'
import { Tabs, Tab, Box, Card, Paper, Typography } from '@mui/material'
import { UploadImageV2 } from './UploadImageV2'
import { TempAutoSelectInput } from '../Input/TempAutoSelectInput'
import { TempTextInput } from '../Input/TempTextInput'
import { TempBtn } from '../Input/TempBtn'
import { getString } from '../../helper/util'

import DeleteIcon from '@mui/icons-material/Delete'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import PropTypes from 'prop-types'

import { setBrandValue } from '../../actions/brandAction'
import { setProductCategory, setProductValue } from '../../actions/productCreateAction'
import { setMerchantShopValue } from '../../actions/merchantShopCreateAction'
import { constants as cons, IMAGE_TYPE } from '../../Constants/constants'
import { setLoading } from '../../actions/systemAction'
import { UploadImage } from './UploadImage'

import maybeClearDependentFields from '../ProductCreate/Product/clearDependentFields'
import I18nImageTabs from '../ProductCreate/Product/cashCouponImageList'

const viewOnlyKey = [
  // 'cashCouponDetailImage',
  // 'cashCouponDetailPurchasedImage'
]

const i18nImageKeys = [
  'iconImage',
  'cashCouponBrandImage',
  'cashCouponLeftImage',
  'cashCouponDetailImage',
  'cashCouponDetailPurchasedImage',
]

// Map product image type constants (from caller) to imageMedia keys
const defaultMapTypeToKey = (type) => {
  switch (type) {
    case 'ITEM_ICON':
      return 'iconImage'
    case 'ITEM_DETAIL':
      return 'detailImageList'
    case 'CASH_COUPON_BRAND_IMAGE':
      return 'cashCouponBrandImage'
    case 'CASH_COUPON_LEFT_IMAGE':
      return 'cashCouponLeftImage'
    case 'CASH_COUPON_DETAIL':
      return 'cashCouponDetailImage'
    case 'CASH_COUPON_PURCHASED_DETAIL':
      return 'cashCouponDetailPurchasedImage'
    default:
      return null
  }
}

const LANGUAGE_KEY_PATTERN = /^[a-z]{2}(?:[-_][a-z]{2})?$/i
const DEFAULT_I18N_LANGUAGES = ['en', 'zh-HK', 'zh-TW', 'zh-CN']

const isLanguageKey = (key) => LANGUAGE_KEY_PATTERN.test(String(key || ''))

const normalizeImageLanguageKey = (languageKey) => {
  switch (languageKey) {
    case 'EN':
    case 'en':
      return 'en'
    case 'ZH_HK':
    case 'zh_HK':
    case 'zh-HK':
      return 'zh-HK'
    case 'ZH_TW':
    case 'zh_TW':
    case 'zh-TW':
      return 'zh-TW'
    case 'ZH_CN':
    case 'zh_CN':
    case 'zh-CN':
      return 'zh-CN'
    default:
      return languageKey
  }
}

const preserveImageIdentityFields = (existingValue, nextValue) => {
  if (!nextValue) {
    return nextValue
  }

  const preservedValue = {
    ...existingValue,
    ...nextValue,
  }

  if (existingValue?.id) {
    preservedValue.id = existingValue.id
  }

  if (existingValue?.batchId) {
    preservedValue.batchId = existingValue.batchId
  }

  return preservedValue
}

const buildReplicatedImageMap = (currentMap = {}, nextValue) => {
  return DEFAULT_I18N_LANGUAGES.reduce((accumulator, languageKey) => {
    accumulator[languageKey] = preserveImageIdentityFields(currentMap?.[languageKey], _.cloneDeep(nextValue))
    return accumulator
  }, {})
}

const createEmptyI18nImageMap = () => {
  return DEFAULT_I18N_LANGUAGES.reduce((accumulator, languageKey) => {
    accumulator[languageKey] = {
      cosFileKey: null,
      cosFileUrl: null,
      type: 'ITEM_DETAIL',
      status: 'A',
      displaySequence: 10,
    }
    return accumulator
  }, {})
}

// Normalize a tab's value from imageMedia into an array for rendering
const getListForTab = (imageMedia = {}, tabKey) => {
  if (!tabKey) return []
  const value = imageMedia[tabKey]

  if (!value) return []

  // If value is an object: could be language-keyed map or single object
  if (value && !Array.isArray(value) && typeof value === 'object') {
    const keys = Object.keys(value)
    const looksLikeLangMap = keys.length > 0 && keys.every((key) => isLanguageKey(key))
    if (looksLikeLangMap) {
      return keys.map((lang) => ({ lang, data: value[lang] }))
    }
    // Single-image object (e.g., leftImage) -> return single entry with no lang
    return [{ lang: null, data: value }]
  }

  if (Array.isArray(value)) {
    // Legacy arrays - map to { lang: null, data }
    return value.map((d) => ({ lang: d.language || null, data: d }))
  }

  return []
}

// clear coupon detail image and purchased detail image if one of them is changed
const checkAndClearCouponDetailImages = (tabKey, nextImageMedia) => {

  if (tabKey === 'cashCouponDetailImage' && !nextImageMedia?.cashCouponDetailPurchasedImage?.en?.cosFileKey) {
    nextImageMedia.cashCouponDetailPurchasedImage = {}
  }

  if (tabKey === 'cashCouponDetailPurchasedImage' && !nextImageMedia?.cashCouponDetailImage?.en?.cosFileKey) {
    nextImageMedia.cashCouponDetailImage = {}
  }
}

export function ImageListCashCoupon(props) {
  const dispatch = useDispatch()
  // Using MUI v5: prefer `sx` on components instead of makeStyles
  const {
    type,
    typeList = [],
    savedData = {},
    onUploadImageList = () => {},
    uploadPath,
    disableInput,
    resource,
    mediaType,
    hiddenInput,
    validation,
    validationConstants,
    helperTextStylesClasses,
    hideTitle,
    // pass-through props for UploadImageV2
    ...rest
  } = props

  // Support both `imageMedia` and legacy `cashCouponMedia` on the product
  const product = (((savedData || {}).draft || {}).product) || {}
  const imageMedia = product.imageMedia || product.cashCouponMedia || {}
  // active tab index for UI
  const [tabIndex, setTabIndex] = React.useState(0)

  const handleChange = (e, newIndex) => setTabIndex(newIndex)

  const updateImageMedia = (tabKey, lang, updater) => {
    const nextImageMedia = _.cloneDeep(imageMedia || {})
    const currentValue = nextImageMedia[tabKey]
    const normalizedLang = lang ? normalizeImageLanguageKey(lang) : null

    const currentValueAsLanguageMap = () => {
      if (currentValue && !Array.isArray(currentValue) && typeof currentValue === 'object' && !(currentValue.cosFileUrl || currentValue.cosFileKey)) {
        return _.cloneDeep(currentValue)
      }

      if (currentValue && !Array.isArray(currentValue) && typeof currentValue === 'object') {
        return buildReplicatedImageMap({}, currentValue)
      }

      return {}
    }

    if (normalizedLang) {
      const currentMap = currentValue && !Array.isArray(currentValue) && typeof currentValue === 'object' && !(currentValue.cosFileUrl || currentValue.cosFileKey)
        ? _.cloneDeep(currentValue)
        : {}

      const nextValue = updater(currentMap[normalizedLang], currentMap)

      if (i18nImageKeys.includes(tabKey)) {
        if (nextValue) {
          nextImageMedia[tabKey] = buildReplicatedImageMap(currentMap, nextValue)
          
          checkAndClearCouponDetailImages(tabKey, nextImageMedia)
        } else {
          delete nextImageMedia[tabKey]
        }
      } else {
        if (nextValue) {
          currentMap[normalizedLang] = nextValue
        } else {
          delete currentMap[normalizedLang]
        }

        nextImageMedia[tabKey] = currentMap
      }
    } else {
      const nextValue = updater(currentValue, currentValue)
      if (nextValue) {
        if (i18nImageKeys.includes(tabKey)) {
          const currentMap = currentValueAsLanguageMap()

          nextImageMedia[tabKey] = buildReplicatedImageMap(currentMap, nextValue)

          checkAndClearCouponDetailImages(tabKey, nextImageMedia)
        } else {
          nextImageMedia[tabKey] = nextValue
        }
      } else {
        delete nextImageMedia[tabKey]
      }
    }

    dispatch(setProductValue(cons.ACTION_KEY.PRODUCT.PRODUCT, savedData, 'imageMedia', nextImageMedia))

    try {
        // left image change can affect generated cash coupon images
        maybeClearDependentFields(dispatch, product, tabKey)
    } catch (err) {
        // ignore
        console.warn('maybeClearDependentFields failed', err)
    }
  }

  const updateDetailImageList = (itemIndex, lang, updater) => {
    const normalizedLang = lang ? normalizeImageLanguageKey(lang) : 'en'
    const nextImageMedia = _.cloneDeep(imageMedia || {})
    const currentDetailList = Array.isArray(nextImageMedia.detailImageList)
      ? _.cloneDeep(nextImageMedia.detailImageList)
      : []

    while (currentDetailList.length <= itemIndex) {
      currentDetailList.push(createEmptyI18nImageMap())
    }

    const currentItem = currentDetailList[itemIndex] || createEmptyI18nImageMap()
    const currentLangValue = currentItem?.[normalizedLang]
    const nextLangValue = updater(currentLangValue, currentItem)

    if (nextLangValue) {
      const replicatedItem = DEFAULT_I18N_LANGUAGES.reduce((accumulator, languageKey) => {
        accumulator[languageKey] = preserveImageIdentityFields(currentItem?.[languageKey], _.cloneDeep(nextLangValue))
        return accumulator
      }, {})
      currentDetailList[itemIndex] = replicatedItem
    } else {
      if (currentDetailList.length > 1) {
        currentDetailList.splice(itemIndex, 1)
      } else {
        // currentDetailList[itemIndex] = createEmptyI18nImageMap()
        currentDetailList[itemIndex] = null
      }
    }

    const currentItemAfterUpdate = currentDetailList[itemIndex]
    const hasAnyImage = Object.values(currentItemAfterUpdate || {}).some((value) => value && (value.cosFileKey || value.cosFileUrl || value.id))
    if (!hasAnyImage && currentDetailList.length > 1) {
      currentDetailList.splice(itemIndex, 1)
    }
    nextImageMedia.detailImageList = (itemIndex == 0 && !currentDetailList[itemIndex]) ? null : currentDetailList
    dispatch(setProductValue(cons.ACTION_KEY.PRODUCT.PRODUCT, savedData, 'imageMedia', nextImageMedia))
  }

  function getUploadedImagePayload(nameOrPayload, uploadedImage) {
    if (uploadedImage && typeof uploadedImage === 'object' && !Array.isArray(uploadedImage)) {
      return uploadedImage
    }

    if (Array.isArray(nameOrPayload)) {
      return nameOrPayload[0] || null
    }

    if (nameOrPayload && typeof nameOrPayload === 'object') {
      if (nameOrPayload.picture && typeof nameOrPayload.picture === 'object') {
        return nameOrPayload.picture
      }

      if (nameOrPayload.cosFileKey || nameOrPayload.cosFileUrl) {
        return nameOrPayload
      }
    }

    return null
  }

  function onSaveImage(tabKey, lang, nameOrPayload, uploadedImage, itemIndex = 0) {
    const uploadedPayload = getUploadedImagePayload(nameOrPayload, uploadedImage)

    if (!uploadedPayload) {
      return
    }

    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => ({
        ...(currentValue || {}),
        ...uploadedPayload,
        type: uploadedPayload?.type || currentValue?.type || dataTypeFromTabKey(tabKey) || tabKey,
      }))
      return
    }

    updateImageMedia(tabKey, lang, () => ({
      ...imageMedia?.[tabKey]?.[lang],
      ...uploadedPayload,
      type: uploadedPayload?.type || imageMedia?.[tabKey]?.[lang]?.type || dataTypeFromTabKey(tabKey) || tabKey,
      ...((tabKey === 'cashCouponLeftImage' || tabKey === 'cashCouponBrandImage') ? { __updated: true } : {}),
    }))
  }

  function onChangeImageType(tabKey, lang, imageType, itemIndex = 0) {
    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => currentValue ? { ...currentValue, type: imageType } : currentValue)
      return
    }

    updateImageMedia(tabKey, lang, (currentValue) => currentValue ? { ...currentValue, type: imageType } : currentValue)
  }

  function handleImageStatus(tabKey, lang, status, itemIndex = 0) {
    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => currentValue ? { ...currentValue, status: status || '' } : currentValue)
      return
    }

    updateImageMedia(tabKey, lang, (currentValue) => currentValue ? { ...currentValue, status: status || '' } : currentValue)
  }

  function onDeleteImage(tabKey, lang, itemIndex = 0) {
    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => currentValue ? { ...currentValue, status: 'D' } : currentValue)
      return
    }

    updateImageMedia(tabKey, lang, (currentValue) => currentValue ? { ...currentValue, status: 'D' } : currentValue)
  }

  function dataTypeFromTabKey(tabKey) {
    switch (tabKey) {
      case 'iconImage':
        return 'ITEM_ICON'
      case 'detailImage':
      case 'detailImageList':
        return 'ITEM_DETAIL'
      case 'cashCouponBrandImage':
        return 'CASH_COUPON_BRAND_IMAGE'
      case 'leftImage':
      case 'cashCouponLeftImage':
        return 'CASH_COUPON_LEFT_IMAGE'
      case 'cashCouponDetailImage':
        return 'CASH_COUPON_DETAIL'
      case 'cashCouponDetailPurchasedImage':
        return 'CASH_COUPON_PURCHASED_DETAIL'
      default:
        return null
    }
  }

  function EmptyMediaBox() {
    return (
      <Box
        sx={{
          marginTop: 2,
          width: '100%',
          maxWidth: 600,
          height: 300,
          bgcolor: 'action.hover',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1">No media available</Typography>
      </Box>
    )
  }

  // Render content for a tab (product-only behavior)
  const renderProductTab = (tabItem) => {
    const raw = typeof tabItem === 'string' ? tabItem : (tabItem && (tabItem.value || tabItem.key || tabItem))
    const tabKey = defaultMapTypeToKey(raw)
    if (!tabKey) return null

    const list = imageMedia[tabKey]

    if (tabKey === 'detailImageList') {
      // const detailList = Array.isArray(list) && list.length ? list : [createEmptyI18nImageMap()]
      const detailList = Array.isArray(list) && list.length ? list : null
      return (
        <div key={tabKey}>
          {
            detailList ? (
              <>
                {detailList.map((detailItem, detailIndex) => (
                  <Box key={`${tabKey}-${detailIndex}`} sx={{ mb: 2 }}>
                    {
                      detailItem && Object.values(detailItem).every((value) => !value || !value.cosFileUrl) ? (
                        <UploadImageV2
                          {...rest}
                          picture={null}
                          type={type}
                          title={{ resource: 'UploadImage', key: 'image' }}
                          name="image"
                          uploadImagePath={uploadPath}
                          saveImage={(name, image) => onSaveImage(tabKey, null, name, image, detailIndex)}
                          disableInput={disableInput}
                        />
                      ) : Object.values(detailItem).every((value) => value?.status !== 'A') ? (
                        <></>
                      ) : (
                        <I18nImageTabs
                          templateData={detailItem}
                          tabKey={tabKey}
                          itemIndex={detailIndex}
                          onSaveImage={onSaveImage}
                          onDeleteImage={onDeleteImage}
                          onChangeImageType={onChangeImageType}
                          handleImageStatus={handleImageStatus}
                          {...props}
                        />
                      )
                    }
                    
                  </Box>
                ))}

                {!disableInput && (
                  <Box sx={{ mt: 1 }}>
                    <TempBtn
                      btnSetting={{
                        className: 'edit-create-btn',
                        variant: cons.STYLE.VARIANT.CONTAINED,
                        color: cons.STYLE.PRIMARY,
                        label: { resource: 'productCreateProductList', key: 'add' },
                        onClick: () => {
                          const nextImageMedia = _.cloneDeep(imageMedia || {})
                          const currentDetailList = Array.isArray(nextImageMedia.detailImageList) ? nextImageMedia.detailImageList : []
                          currentDetailList.push(createEmptyI18nImageMap())
                          nextImageMedia.detailImageList = currentDetailList
                          dispatch(setProductValue(cons.ACTION_KEY.PRODUCT.PRODUCT, savedData, 'imageMedia', nextImageMedia))
                        },
                        disabled: disableInput,
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box>
                {
                  viewOnlyKey.includes(tabKey) ? (
                    <EmptyMediaBox />
                  ) : (
                    <UploadImageV2
                      {...rest}
                      picture={null}
                      type={type}
                      title={{ resource: 'UploadImage', key: 'image' }}
                      name="image"
                      uploadImagePath={uploadPath}
                      saveImage={(name, image) => onSaveImage(tabKey, null, name, image)}
                      disableInput={disableInput}
                    />
                  )
                }
                
              </Box>
            )
          }
        </div>
      )
    }

    return (
      <div 
      // className={classes.tabPanel} 
      key={tabKey}>
        {
          list && !_.isEmpty(list) ? (
            <I18nImageTabs
              templateData={list}
              onSaveImage={onSaveImage}
              onDeleteImage={onDeleteImage}
              onChangeImageType={onChangeImageType}
              handleImageStatus={handleImageStatus}
              tabKey={tabKey}
              canDelete={false}
              {...props}
            />
          ) : (
            <Box>
              {
                viewOnlyKey.includes(tabKey) ? (
                  <EmptyMediaBox />
                ) : (
                  <UploadImageV2
                    {...rest}
                    picture={null}
                    type={type}
                    title={{ resource: 'UploadImage', key: 'image' }}
                    name="image"
                    uploadImagePath={uploadPath}
                    saveImage={(name, image) => onSaveImage(tabKey, null, name, image)}
                    disableInput={disableInput}
                  />
                )
              }
              
            </Box>
          )
        }
      </div>
    )
  }

  const isProduct = (typeof type === 'string' && type.toUpperCase() === 'PRODUCT')

  return (
    <div>
      {typeList && typeList.length ? (
        <Tabs value={tabIndex} onChange={handleChange} indicatorColor="primary" textColor="primary" variant="scrollable">
          {typeList.map((t, i) => (
            <Tab key={`${(t && (t.value || t.key)) || i}`} label={(t && (t.label || t.value || t.key)) || t} />
          ))}
        </Tabs>
      ) : null}

      <div>
        {typeList && typeList.length && isProduct ? renderProductTab(typeList[tabIndex]) : null}
        {(!isProduct && typeList && typeList.length) && (
          <Box sx={{ pt: 1 }}>
            <UploadImageV2 
              {...rest} 
              title={{ resource: 'UploadImage', key: 'image' }}
              onSave={(img) => onUploadImageList({ tab: null, image: img, action: 'legacySave' })} 
              />
          </Box>
        )}
      </div>
    </div>
  )
}
